// src/components/TelemedRoom.jsx
// Version finale :
// - Ouverture de la réunion uniquement 10 min avant l’heure du RDV (gérée par le backend ; UI avec compte à rebours)
// - Auto-start/stop de la transcription (déclenchée côté professionnel quand ≥ 2 participants)
// - Résumé "texte métier" affiché uniquement pour le professionnel
// - Avertissement médical (en-US obligatoire) conservé
// - Consentement patient obligatoire
// - Dates : backend renvoie des ISO UTC ; front parse en UTC et affiche en America/Toronto (Montréal)

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod';

/* ================= Helpers Chime ================= */
async function bindAudioOutputCompat(audioVideo, audioEl, deviceId) {
  if (!audioVideo || !audioEl) return;
  await audioVideo.bindAudioElement(audioEl);
  if (typeof audioVideo.chooseAudioOutputDevice === 'function') {
    if (deviceId) await audioVideo.chooseAudioOutputDevice(deviceId).catch(() => {});
  } else if (audioEl && 'setSinkId' in audioEl && deviceId) {
    await audioEl.setSinkId(deviceId).catch(() => {});
  }
}
async function chooseAudioInputCompat(audioVideo, device) {
  if (!audioVideo || !device) return;
  if (typeof audioVideo.chooseAudioInputDevice === 'function') {
    return audioVideo.chooseAudioInputDevice(device.deviceId ?? device);
  }
  if (typeof audioVideo.startAudioInput === 'function') {
    return audioVideo.startAudioInput(device);
  }
}
async function chooseVideoInputCompat(audioVideo, device) {
  if (!audioVideo || !device) return;
  if (typeof audioVideo.chooseVideoInputDevice === 'function') {
    return audioVideo.chooseVideoInputDevice(device.deviceId ?? device);
  }
  if (typeof audioVideo.startVideoInput === 'function') {
    return audioVideo.startVideoInput(device);
  }
}

/* ================= Helpers heure MTL (parse UTC, affiche MTL) ================= */

// Force l'interprétation UTC (ajoute 'Z' si besoin)
function parseUtc(isoOrSql) {
  if (!isoOrSql) return null;
  const s = isoOrSql.includes('T') ? isoOrSql : isoOrSql.replace(' ', 'T') + 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// Format MTL HH:mm
function fmtMtlTime(isoUtc) {
  const d = parseUtc(isoUtc);
  if (!d) return '';
  return new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'America/Toronto',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).format(d);
}

// Format MTL date + heure
function fmtMtlDateTime(isoUtc) {
  const d = parseUtc(isoUtc);
  if (!d) return '';
  return new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'America/Toronto',
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: false
  }).format(d);
}

// Secondes restantes avant une date UTC
function secondsUntilUtc(isoUtc) {
  const d = parseUtc(isoUtc);
  if (!d) return 0;
  const now = Date.now();
  const diff = Math.ceil((d.getTime() - now) / 1000);
  return diff > 0 ? diff : 0;
}

/* ================= Options UI ================= */
const LANGS = [
  { value: 'fr-CA', label: 'Français (Canada)' },
  { value: 'fr-FR', label: 'Français (France)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'es-ES', label: 'Español' },
  { value: 'de-DE', label: 'Deutsch' },
];
const MODES = [
  { value: 'standard', label: 'Standard' },
  { value: 'medical', label: 'Médical (en-US seulement)' },
];

/* =========================================
 * Transformations "texte métier" du résumé
 * ========================================= */
function normalizeTokenFr(s='') {
  const t = s
    .toLowerCase()
    .replace(/^j['’]\s*/,'')
    .replace(/\b(le|la|les|des|du|de|d’|d'|un|une|au|aux|mon|ma|mes|ton|ta|tes|son|sa|ses|ce|cet|cette|ces)\b/gi,'')
    .replace(/[.,;:!?]+/g,' ')
    .replace(/\s+/g,' ')
    .trim();
  return t;
}
const SYMPTOM_LEXICON_FR = [
  { k:/douleur|douleurs|mal/g, label:'douleur' },
  { k:/toux/g, label:'toux' },
  { k:/fièvre|fievre/g, label:'fièvre' },
  { k:/courbature(s)?/g, label:'courbatures' },
  { k:/étourdissement(s)?|vertige(s)?/g, label:'étourdissements' },
  { k:/nausée(s)?|vomissement(s)?/g, label:'nausées/vomissements' },
  { k:/fatigue|asthénie/g, label:'fatigue' },
  { k:/thoracique(s)?|poitrine/g, label:'douleur thoracique' },
  { k:/maux?\s+de\s+tête|céphalée(s)?/g, label:'maux de tête' },
];
function extractSymptomsFR(phrases=[]) {
  const hit = new Map();
  for (const p of phrases) {
    const txt = normalizeTokenFr(p?.text || '');
    if (!txt) continue;
    for (const rule of SYMPTOM_LEXICON_FR) {
      if (rule.k.test(txt)) {
        hit.set(rule.label, (hit.get(rule.label) || 0) + 1);
      }
    }
  }
  return Array.from(hit.entries())
    .sort((a,b)=>b[1]-a[1])
    .map(([label,count])=>({ label, count }));
}
function prettySentimentLabel(s, locale='fr') {
  const L = String(s||'NEUTRAL').toUpperCase();
  if (locale.startsWith('fr')) {
    if (L==='POSITIVE') return 'POSITIF';
    if (L==='NEGATIVE') return 'NÉGATIF';
    if (L==='MIXED')    return 'MIXTE';
    return 'NEUTRE';
  }
  return L;
}
function buildClinicalNarrativeFromSummary(summary, locale='fr') {
  const mode = summary?.transcription?.job_mode || 'standard';
  const langLc = (summary?.transcription?.language_code || '').toLowerCase();
  const sentimentLabel = prettySentimentLabel(summary?.sentiment?.label, locale);
  const sentimentLine = summary?.sentiment
    ? `Perception globale : ${sentimentLabel} (Positif ${summary.sentiment.scores.positive} / Négatif ${summary.sentiment.scores.negative} / Neutre ${summary.sentiment.scores.neutral} / Mixte ${summary.sentiment.scores.mixed}).`
    : null;

  if (mode==='medical' && langLc==='en-us') {
    const entities = summary?.top?.entities || [];
    const icd10   = summary?.top?.icd10cm || [];
    const rxnorm  = summary?.top?.rxnorm || [];

    const keyConditions = entities
      .filter(e => (e.category||'').includes('MEDICAL') || (e.type||'').includes('DX'))
      .slice(0, 3)
      .map(e => `${e.text} — ${e.category}${e.type ? ` / ${e.type}` : ''}`);

    const meds = rxnorm.slice(0,3).map(r => `${r.description} (${r.code})`);
    const icds = icd10.slice(0,5).map(c => `${c.code} — ${c.description}${c.count ? ` (x${c.count})` : ''}`);

    const bullets = [];
    if (keyConditions.length) bullets.push(`Diagnostics/conditions évoqués : ${keyConditions.join(' ; ')}`);
    if (meds.length) bullets.push(`Médicaments mentionnés : ${meds.join(' ; ')}`);
    if (icds.length) bullets.push(`Codes ICD-10-CM : ${icds.join(' ; ')}`);
    if (sentimentLine) bullets.push(sentimentLine);

    return bullets.length ? bullets : ['Aucun élément saillant détecté.'];
  }

  const keyPhrases  = summary?.top?.key_phrases || [];
  const symptoms = (locale.startsWith('fr') || langLc.startsWith('fr'))
    ? extractSymptomsFR(keyPhrases) : [];

  const bullets = [];
  if (keyPhrases.length) {
    const clean = keyPhrases
      .map(k => ({ text: normalizeTokenFr(k.text), score:k.top_score, c:k.occurrences }))
      .filter(k => k.text && k.text.length>1)
      .slice(0,5)
      .map(k => `${k.text} (x${k.c}, score ${k.score})`);
    bullets.push(`Expressions clés : ${clean.join(' ; ')}`);
  }
  if (symptoms.length) bullets.push(`Symptômes évoqués : ${symptoms.map(s=>`${s.label}${s.count>1?` (x${s.count})`:''}`).join(' ; ')}`);
  if (sentimentLine) bullets.push(sentimentLine);
  return bullets.length ? bullets : ['Aucun élément saillant détecté.'];
}
function buildHumanSummary(summary) {
  if (!summary?.ready) return [];
  return buildClinicalNarrativeFromSummary(summary, 'fr');
}

/* ================= Component ================= */
export default function TelemedRoom() {
  const { sessionId } = useParams();

  /* ---------- État global UI ---------- */
  const [uiRole, setUiRole] = useState('patient'); // patient | professional
  const [patientConsented, setPatientConsented] = useState(false);

  const [status, setStatus] = useState('Initialisation…');
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [ready, setReady] = useState(false);
  const [joined, setJoined] = useState(false);
  const [needAudioResume, setNeedAudioResume] = useState(false);

  /* ---------- Devices ---------- */
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [speakerDevices, setSpeakerDevices] = useState([]);
  const [selMic, setSelMic] = useState('');
  const [selCam, setSelCam] = useState('');
  const [selSpk, setSelSpk] = useState('');

  const [muted, setMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(true);
  const [sharing, setSharing] = useState(false);

  /* ---------- Transcription (auto) ---------- */
  const [tLang, setTLang] = useState('fr-CA');
  const [tMode, setTMode] = useState('standard');
  const [saving, setSaving] = useState(false);

  const [pipelineId, setPipelineId] = useState('');
  const [pipelineStatus, setPipelineStatus] = useState('');
  const startingRef = useRef(false);
  const runningRef  = useRef(false);

  /* ---------- Présence + vu-mètre ---------- */
  const [attendeeCount, setAttendeeCount] = useState(0);
  const presentSetRef = useRef(new Set());
  const [micLevel, setMicLevel] = useState(0);

  /* ---------- Résumé ---------- */
  const [summary, setSummary] = useState(null);
  const [sumLoading, setSumLoading] = useState(false);
  const [sumError, setSumError] = useState('');

  /* ---------- Gate (heure RDV) ---------- */
  // États renvoyés par le backend credentials: "allowed" | "too_early" | "too_late" | "error"
  const [gateStatus, setGateStatus] = useState('unknown');
  const [startsAtISO, setStartsAtISO]   = useState(null); // heure du rdv (ISO UTC)
  const [allowFromISO, setAllowFromISO] = useState(null); // ouverture de la salle (ISO UTC)
  const [countdownSec, setCountdownSec] = useState(0);
  const countdownTimerRef = useRef(null);

  /* ---------- Refs DOM/Meeting ---------- */
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const audioRef = useRef(null);

  const meetingRef = useRef(null);
  const attendeeRef = useRef(null);
  const meetingSessionRef = useRef(null);
  const audioVideoRef = useRef(null);
  const videoObserverRef = useRef(null);
  const meetingObserverRef = useRef(null);

  /* ---------- Dérivés ---------- */
  const isMedicalInvalid = useMemo(
    () => tMode === 'medical' && tLang !== 'en-US',
    [tMode, tLang]
  );
  const proCanSeeSummary = uiRole === 'professional';

  /* ---------- Rôle UI ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const role = String(params.get('role') || 'patient').toLowerCase();
    setUiRole(['patient', 'professional'].includes(role) ? role : 'patient');
  }, []);

  /* ---------- Countdown manager ---------- */
  const stopCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);
  useEffect(() => {
    return () => stopCountdown();
  }, [stopCountdown]);

  /* ---------- Credentials (avec gestion heure RDV) ---------- */
  const fetchCredentials = useCallback(async () => {
    setError('');
    setConnected(false);
    setJoined(false);
    setStatus('Récupération des identifiants de réunion…');

    stopCountdown();

    try {
      const params = new URLSearchParams(window.location.search);
      const role = params.get('role');
      const token = params.get('token');
      if (!role || !token) throw new Error('Lien invalide (role/token manquants)');

      const resp = await fetch(
        `${API_BASE}/telemedicine-sessions/${sessionId}/credentials?role=${encodeURIComponent(
          role
        )}&token=${encodeURIComponent(token)}`
      );

      if (resp.status === 403) {
        const data = await resp.json().catch(() => ({}));
        const code = String(data?.code || '').toLowerCase();

        if (code === 'too_early') {
          // Respecter Retry-After si le backend l’envoie
          const retryHeader = Number(resp.headers.get('Retry-After') || '15');
          const retryAfterSec = Number.isFinite(retryHeader) ? Math.max(5, Math.min(60, retryHeader)) : 15;

          setGateStatus('too_early');
          setStartsAtISO(data.starts_at || null);
          setAllowFromISO(data.allow_from || null);

          setStatus(
            `Votre rendez-vous commence à ${fmtMtlTime(data.starts_at)}. ` +
            `Vous pourrez rejoindre à partir de ${fmtMtlTime(data.allow_from)}.`
          );

          // Compte à rebours vers allow_from (UTC)
          const tick = () => {
            const s = secondsUntilUtc(data.allow_from);
            setCountdownSec(s);
            if (s === 0) {
              stopCountdown();
              // auto retry
              fetchCredentials();
            }
          };
          tick();
          countdownTimerRef.current = setInterval(tick, 1000);

          // Et re-tente côté réseau selon Retry-After (sécurité)
          setTimeout(() => fetchCredentials(), retryAfterSec * 1000);

          setReady(false);
          return;
        }

        if (code === 'too_late') {
          setGateStatus('too_late');
          setStartsAtISO(data.starts_at || null);
          setStatus(
            `Votre rendez-vous était prévu le ${fmtMtlDateTime(data.starts_at)}. ` +
            `La salle est désormais fermée.`
          );
          setReady(false);
          return;
        }

        // autre refus
        setGateStatus('error');
        setReady(false);
        setError(data?.error || 'Accès refusé');
        setStatus('Accès refusé');
        return;
      }

      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`credentials HTTP ${resp.status}${body ? `: ${body}` : ''}`);
      }

      const { meeting, attendee, starts_at, allow_from } = await resp.json();
      meetingRef.current = meeting;
      attendeeRef.current = attendee;

      setGateStatus('allowed');
      setStartsAtISO(starts_at || null);
      setAllowFromISO(allow_from || null);
      setReady(true);
      setStatus('Prêt — Cliquez sur « Rejoindre »');
    } catch (e) {
      console.error('Telemed (credentials) error:', e);
      setError(e.message || 'Erreur');
      setStatus('Erreur de connexion');
      setReady(false);
    }
  }, [sessionId, stopCountdown]);

  useEffect(() => {
    fetchCredentials();
    return () => stopCountdown();
  }, [fetchCredentials, stopCountdown]);

  /* ---------- Start/Join réunion ---------- */
  const startMeeting = useCallback(async () => {
    setStatus('Initialisation des médias…');

    const logger = new ConsoleLogger('KMQ-Chime', LogLevel.INFO);
    const deviceController = new DefaultDeviceController(logger);
    const configuration = new MeetingSessionConfiguration(meetingRef.current, attendeeRef.current);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
    const audioVideo = meetingSession.audioVideo;

    meetingSessionRef.current = meetingSession;
    audioVideoRef.current = audioVideo;

    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      tmp.getTracks().forEach(t => t.stop());
    } catch (err) {
      console.warn('getUserMedia refused/failed:', err?.name || err);
    }

    const [mics, cams, spks] = await Promise.all([
      audioVideo.listAudioInputDevices(),
      audioVideo.listVideoInputDevices(),
      audioVideo.listAudioOutputDevices(),
    ]);
    setAudioDevices(mics);
    setVideoDevices(cams);
    setSpeakerDevices(spks);
    const mic0 = mics[0]?.deviceId || '';
    const cam0 = cams[0]?.deviceId || '';
    const spk0 = spks[0]?.deviceId || '';
    setSelMic(mic0);
    setSelCam(cam0);
    setSelSpk(spk0);

    if (mics[0]) await chooseAudioInputCompat(audioVideo, mics[0]);
    if (cams[0]) await chooseVideoInputCompat(audioVideo, cams[0]);

    if (audioRef.current) {
      try {
        await bindAudioOutputCompat(audioVideo, audioRef.current, spk0);
      } catch {
        setNeedAudioResume(true);
      }
    }

    const CHIME_STOP_LABEL = {
      Left: 'Vous avez quitté la réunion',
      Dropped: 'Connexion interrompue',
      AudioCallEnded: 'Appel audio terminé',
      MeetingEnded: 'Réunion terminée par l’organisateur',
      AudioJoinedFromAnotherDevice: 'Rejoint depuis un autre appareil',
    };

    const meetingObserver = {
      audioVideoDidStart: () => { setConnected(true); setStatus('Connecté ✅'); },
      audioVideoDidStop: async (sessionStatus) => {
        let code = '';
        try {
          code = typeof sessionStatus?.statusCode === 'function'
            ? sessionStatus.statusCode()
            : sessionStatus?.statusCode || '';
        } catch {}
        const friendly = CHIME_STOP_LABEL[code] || code || '';
        setStatus(friendly ? `Session arrêtée — ${friendly}` : 'Session arrêtée');
        setConnected(false);
        setJoined(false);

        // ⛔️ Stop transcription auto si en cours
        if (runningRef.current) {
          try { await autoStopTranscription(); } catch {}
        }
      },
      connectionDidBecomePoor: () => setStatus('Connexion dégradée…'),
      connectionDidRecover: () => setStatus('Connecté ✅'),
    };
    const videoObserver = {
      videoTileDidUpdate: tileState => {
        if (!tileState || !tileState.boundAttendeeId) return;
        if (tileState.localTile && localVideoRef.current) {
          audioVideo.bindVideoElement(tileState.tileId, localVideoRef.current);
        }
        if (!tileState.localTile && !tileState.isContent && remoteVideoRef.current) {
          audioVideo.bindVideoElement(tileState.tileId, remoteVideoRef.current);
        }
      },
      videoTileWasRemoved: () => {},
    };
    audioVideo.addObserver(meetingObserver);
    audioVideo.addObserver(videoObserver);
    meetingObserverRef.current = meetingObserver;
    videoObserverRef.current = videoObserver;

    const presenceHandler = (attendeeId, present) => {
      if (present) presentSetRef.current.add(attendeeId);
      else presentSetRef.current.delete(attendeeId);
      setAttendeeCount(presentSetRef.current.size);
    };
    if (typeof audioVideo.realtimeSubscribeToAttendeeIdPresence === 'function') {
      audioVideo.realtimeSubscribeToAttendeeIdPresence(presenceHandler);
    } else if (typeof audioVideo.realtimeSubscribeToReceiveAttendeeIdPresence === 'function') {
      audioVideo.realtimeSubscribeToReceiveAttendeeIdPresence(presenceHandler);
    }
    meetingSessionRef.current.__presenceHandler = presenceHandler;

    const localAttendeeId =
      attendeeRef.current?.Attendee?.AttendeeId ||
      attendeeRef.current?.attendeeId ||
      attendeeRef.current?.AttendeeId ||
      '';
    if (localAttendeeId && typeof audioVideo.realtimeSubscribeToVolumeIndicator === 'function') {
      audioVideo.realtimeSubscribeToVolumeIndicator(
        localAttendeeId,
        (_attendeeId, volume /* 0..1 */) => {
          if (typeof volume === 'number') setMicLevel(volume);
        }
      );
    }

    try { await audioVideo.start(); } catch { setNeedAudioResume(true); }
    try { await audioVideo.realtimeUnmuteLocalAudio(); setMuted(false); } catch {}
    try { await audioVideo.startLocalVideoTile(); setVideoOn(true); } catch { setVideoOn(false); }

    setJoined(true);
  }, []);

  const handleJoin = useCallback(async () => {
    if (!ready || gateStatus !== 'allowed') return;
    setError('');
    try { await startMeeting(); } catch (e) {
      console.error('startMeeting error:', e);
      setError(e.message || 'Erreur');
      setStatus('Erreur de connexion');
    }
  }, [ready, gateStatus, startMeeting]);

  const handleLeave = useCallback(async () => {
    try {
      // ⛔️ Stop transcription auto si en cours
      if (runningRef.current) {
        try { await autoStopTranscription(); } catch {}
      }
      const av = audioVideoRef.current;
      if (av) {
        av.stopLocalVideoTile?.();
        av.stopContentShare?.();
        av.removeAllVideoTiles?.();
        av.stop?.();
      }
    } catch {}
  }, []);

  /* ---------- Toggles & devices ---------- */
  const toggleMute = useCallback(async () => {
    const av = audioVideoRef.current; if (!av) return;
    try {
      if (muted) { await av.realtimeUnmuteLocalAudio(); setMuted(false); }
      else { await av.realtimeMuteLocalAudio(); setMuted(true); }
    } catch {}
  }, [muted]);

  const toggleVideo = useCallback(async () => {
    const av = audioVideoRef.current; if (!av) return;
    try {
      if (videoOn) { await av.stopLocalVideoTile(); setVideoOn(false); }
      else { await av.startLocalVideoTile(); setVideoOn(true); }
    } catch {}
  }, [videoOn]);

  const toggleShare = useCallback(async () => {
    const av = audioVideoRef.current; if (!av) return;
    try {
      if (sharing) { await av.stopContentShare(); setSharing(false); }
      else { await av.startContentShareFromScreenCapture(); setSharing(true); }
    } catch { setError('Partage d’écran refusé'); }
  }, [sharing]);

  const resumeAudio = useCallback(async () => {
    const av = audioVideoRef.current;
    if (!av || !audioRef.current) return;
    try {
      await bindAudioOutputCompat(av, audioRef.current, selSpk || speakerDevices[0]?.deviceId);
      await audioRef.current.play().catch(() => {});
      setNeedAudioResume(false);
      setStatus('Connecté ✅');
    } catch { setError("Impossible d'activer l'audio (autorise le son pour ce site)"); }
  }, [selSpk, speakerDevices]);

  const onChangeMic = useCallback(async (e) => {
    const id = e.target.value; setSelMic(id);
    const av = audioVideoRef.current; const dev = audioDevices.find(d => (d.deviceId || d) === id);
    if (av) await chooseAudioInputCompat(av, dev || id);
  }, [audioDevices]);

  const onChangeCam = useCallback(async (e) => {
    const id = e.target.value; setSelCam(id);
    const av = audioVideoRef.current; const dev = videoDevices.find(d => (d.deviceId || d) === id);
    if (av) await chooseVideoInputCompat(av, dev || id);
  }, [videoDevices]);

  const onChangeSpk = useCallback(async (e) => {
    const id = e.target.value; setSelSpk(id);
    const av = audioVideoRef.current;
    if (av && audioRef.current) await bindAudioOutputCompat(av, audioRef.current, id);
  }, []);

  /* ---------- Réglages transcription (enregistrer) ---------- */
  const saveTranscriptionSettings = useCallback(async () => {
    if (uiRole !== 'professional') return;
    setSaving(true);
    try {
      const resp = await fetch(`${API_BASE}/telemedicine-sessions/${sessionId}/transcription-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: tLang, mode: tMode })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || 'Erreur de sauvegarde');
      setStatus(`Réglages enregistrés (${tLang}/${tMode})`);
    } catch (e) {
      console.error('saveTranscriptionSettings error:', e);
      setError(e.message || 'Erreur sauvegarde réglages');
    } finally {
      setSaving(false);
    }
  }, [sessionId, tLang, tMode, uiRole]);

  /* ---------- Résumé : fetch + polling ---------- */
  const fetchSummary = useCallback(async () => {
    try {
      setSumError('');
      setSumLoading(true);
      const resp = await fetch(`${API_BASE}/telemedicine-sessions/${sessionId}/transcript/summary?prefer=latest`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Erreur récupération résumé');
      setSummary(data);
      return data;
    } catch (e) {
      setSumError(e.message || 'Erreur résumé');
      return null;
    } finally {
      setSumLoading(false);
    }
  }, [sessionId]);

  const startSummaryPolling = useCallback(async (timeoutMs = 60000, intervalMs = 3000) => {
    const start = Date.now();
    let last = await fetchSummary();
    while (Date.now() - start < timeoutMs) {
      if (last?.ready) break;
      await new Promise(res => setTimeout(res, intervalMs));
      last = await fetchSummary();
    }
    return last;
  }, [fetchSummary]);

  /* ---------- Auto start/stop transcription ---------- */
  const autoStartTranscription = useCallback(async () => {
    if (startingRef.current || runningRef.current) return;
    startingRef.current = true;
    try {
      await fetch(`${API_BASE}/telemedicine-sessions/${sessionId}/transcription-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: tLang, mode: tMode })
      }).catch(() => {});

      const resp = await fetch(`${API_BASE}/telemedicine-sessions/${sessionId}/transcription/start`, { method: 'POST' });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Échec démarrage transcription');

      setPipelineId(data.pipelineId || '');
      setPipelineStatus(data.pipelineStatus || '');
      setStatus(`Transcription démarrée (${data.settings?.language}/${data.settings?.mode})`);
      runningRef.current = true;

      setSummary(null);
      setSumError('');
    } catch (e) {
      console.error('autoStartTranscription error:', e);
      setError(e.message || 'Erreur démarrage transcription');
    } finally {
      startingRef.current = false;
    }
  }, [sessionId, tLang, tMode]);

  const autoStopTranscription = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/telemedicine-sessions/${sessionId}/transcription/stop`, { method: 'POST' });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Échec arrêt transcription');
      setPipelineStatus('Stopped');
      setStatus('Transcription arrêtée');
      runningRef.current = false;

      // Récupération du résumé
      await startSummaryPolling();
    } catch (e) {
      console.error('autoStopTranscription error:', e);
      setError(e.message || 'Erreur arrêt transcription');
      runningRef.current = false;
    }
  }, [sessionId, startSummaryPolling]);

  // Déclenchement automatique quand ≥ 2 participants et pro + non medical invalid
  useEffect(() => {
    if (
      uiRole === 'professional' &&
      joined &&
      attendeeCount >= 2 &&
      !runningRef.current &&
      !startingRef.current &&
      !isMedicalInvalid
    ) {
      autoStartTranscription();
    }
  }, [attendeeCount, joined, uiRole, isMedicalInvalid, autoStartTranscription]);

  /* ---------- Cleanup ---------- */
  useEffect(() => {
    return () => {
      try {
        // Stop auto si on détruit le composant avec transcription active
        if (runningRef.current) {
          fetch(`${API_BASE}/telemedicine-sessions/${sessionId}/transcription/stop`, { method: 'POST' }).catch(()=>{});
          runningRef.current = false;
        }

        const av = audioVideoRef.current || meetingSessionRef.current?.audioVideo;

        if (av && videoObserverRef.current) {
          try { av.removeObserver(videoObserverRef.current); } catch {}
        }
        if (av && meetingObserverRef.current) {
          try { av.removeObserver(meetingObserverRef.current); } catch {}
        }

        const presenceHandler = meetingSessionRef.current?.__presenceHandler;
        if (av && presenceHandler) {
          try {
            if (typeof av.realtimeUnsubscribeToAttendeeIdPresence === 'function') {
              av.realtimeUnsubscribeToAttendeeIdPresence(presenceHandler);
            } else if (typeof av.realtimeUnsubscribeFromReceiveAttendeeIdPresence === 'function') {
              av.realtimeUnsubscribeFromReceiveAttendeeIdPresence(presenceHandler);
            }
          } catch {}
        }

        try { av?.stopLocalVideoTile?.(); } catch {}
        try { av?.stopContentShare?.(); } catch {}
        try { av?.removeAllVideoTiles?.(); } catch {}
        try { av?.stop?.(); } catch {}

        try {
          if (av && typeof av.bindAudioElement === 'function') {
            av.bindAudioElement(null);
          }
        } catch {}

        try { meetingSessionRef.current?.deviceController?.destroy?.(); } catch {}

      } catch (e) {
        console.warn('Cleanup error:', e);
      } finally {
        videoObserverRef.current = null;
        meetingObserverRef.current = null;
        audioVideoRef.current = null;
        meetingSessionRef.current = null;
        presentSetRef.current.clear();
      }
    };
  }, [sessionId]);

  /* ---------- UI ---------- */
  const badgeStyle = useMemo(
    () => ({
      display: 'inline-block',
      marginLeft: 8,
      padding: '2px 8px',
      borderRadius: 12,
      background: uiRole === 'professional' ? '#e2f1ff' : '#e9f7ef',
      color: '#244',
      fontSize: 12,
    }),
    [uiRole]
  );

  const humanNarrative = useMemo(() => buildHumanSummary(summary), [summary]);

  const canJoin = ready && gateStatus === 'allowed' && (uiRole !== 'patient' || patientConsented);

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <h2 style={{ marginBottom: 8 }}>
        Téléconsultation
        <span style={badgeStyle}>{uiRole === 'professional' ? 'Professionnel' : 'Patient'}</span>
      </h2>

      {/* GATE messages (heure RDV) */}
      {gateStatus === 'too_early' && (
        <div style={{ background: '#fff6e5', border: '1px solid #ffd18c', padding: 12, borderRadius: 8, marginBottom: 12, color: '#5f4b00' }}>
          <div style={{ fontWeight: 600 }}>
            La salle ouvrira à {fmtMtlTime(allowFromISO)} (RDV à {fmtMtlTime(startsAtISO)}).
          </div>
          {countdownSec > 0 && (
            <div style={{ marginTop: 4 }}>
              Vous pourrez rejoindre dans <strong>{Math.floor(countdownSec/60)} min {countdownSec%60}s</strong>.
              La page tentera de se connecter automatiquement à l’ouverture.
            </div>
          )}
        </div>
      )}
      {gateStatus === 'too_late' && (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', padding: 12, borderRadius: 8, marginBottom: 12, color: '#721c24' }}>
          La salle est fermée. Le rendez-vous était prévu le {fmtMtlDateTime(startsAtISO)}.
        </div>
      )}
      {gateStatus === 'error' && (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', padding: 12, borderRadius: 8, marginBottom: 12, color: '#721c24' }}>
          {error || 'Accès refusé.'}
        </div>
      )}

      {/* Consentement patient */}
      {uiRole === 'patient' && (
        <div
          style={{
            background: '#fff6e5',
            border: '1px solid #ffd18c',
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
            color: '#5f4b00',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10
          }}
        >
          <input
            id="patient-consent"
            type="checkbox"
            checked={patientConsented}
            onChange={(e) => setPatientConsented(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <label htmlFor="patient-consent" style={{ cursor: 'pointer' }}>
            J’accepte l’enregistrement et la transcription de cette rencontre médicale
            dans le respect des politiques de confidentialité de KnowMediQ.
          </label>
        </div>
      )}

      {/* Réglages transcription (PRO) + Avertissement conservé */}
      {uiRole === 'professional' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            border: '1px solid #eee',
            borderRadius: 8,
            background: '#fafbff',
            marginBottom: 12
          }}
        >
          <div>
            <label style={{ fontSize: 13, color: '#555' }}>Langue</label><br />
            <select
              value={tLang}
              onChange={e => setTLang(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', minWidth: 170 }}
            >
              {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 13, color: '#555' }}>Mode</label><br />
            <select
              value={tMode}
              onChange={e => setTMode(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', minWidth: 200 }}
            >
              {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          <button
            onClick={saveTranscriptionSettings}
            disabled={saving}
            style={{
              background: '#6c43f3',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              padding: '8px 14px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>

          {/* ✅ Avertissement CONSERVÉ */}
          {isMedicalInvalid && (
            <span style={{ color: '#b71c1c', fontSize: 13 }}>
              Le mode <strong>médical</strong> exige la langue <strong>en-US</strong>.
            </span>
          )}
        </div>
      )}

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <span style={{ color: connected ? 'green' : '#999' }}>
          {connected ? 'Connecté ✅' : 'Hors ligne'}
        </span>
        <span style={{ color: '#666' }}>
          {status} {!joined && ready && gateStatus === 'allowed' ? ' — Cliquez sur « Rejoindre »' : ''}
        </span>
      </div>
      {error ? <p style={{ color: 'crimson', marginTop: 0 }}>{error}</p> : null}

      {/* bouton rejoindre */}
      {!joined && (
        <div style={{ margin: '8px 0 16px' }}>
          <button
            onClick={handleJoin}
            disabled={!canJoin}
            style={{
              background: !canJoin ? '#aaa' : '#6c43f3',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              padding: '10px 16px',
              cursor: !canJoin ? 'not-allowed' : 'pointer'
            }}
          >
            Rejoindre la réunion
          </button>
        </div>
      )}

      {needAudioResume && (
        <div style={{ background: '#fff6e5', border: '1px solid #ffd18c', padding: 10, borderRadius: 6, marginBottom: 12 }}>
          Le navigateur a bloqué le son automatique.
          <button onClick={resumeAudio} style={{ marginLeft: 10, padding: '6px 10px' }}>
            Activer le son
          </button>
        </div>
      )}

      {/* zone vidéos + devices */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h4>Moi</h4>
          <video
            ref={localVideoRef}
            style={{ width: 360, height: 240, background: '#111' }}
            autoPlay
            playsInline
            muted
          />
        </div>
        <div>
          <h4>Autre participant</h4>
          <video
            ref={remoteVideoRef}
            style={{ width: 360, height: 240, background: '#111' }}
            autoPlay
            playsInline
          />
        </div>
      </div>

      {/* Sélecteurs devices */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 13, color: '#555' }}>Micro</label><br />
          <select value={selMic} onChange={onChangeMic} style={{ padding: 6, minWidth: 220 }}>
            {audioDevices.map(d => (
              <option key={d.deviceId || d} value={d.deviceId || d}>{d.label || d.deviceId || 'Micro'}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, color: '#555' }}>Caméra</label><br />
          <select value={selCam} onChange={onChangeCam} style={{ padding: 6, minWidth: 220 }}>
            {videoDevices.map(d => (
              <option key={d.deviceId || d} value={d.deviceId || d}>{d.label || d.deviceId || 'Caméra'}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, color: '#555' }}>Haut-parleur</label><br />
          <select value={selSpk} onChange={onChangeSpk} style={{ padding: 6, minWidth: 220 }}>
            {speakerDevices.map(d => (
              <option key={d.deviceId || d} value={d.deviceId || d}>{d.label || d.deviceId || 'Haut-parleur'}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Vu-mètre micro */}
      <div style={{ marginBottom: 10, color: '#666', fontSize: 12 }}>
        Micro (vu-mètre)&nbsp;
        <span style={{ display: 'inline-block', width: 160, height: 8, background: '#eee', borderRadius: 4, verticalAlign: 'middle' }}>
          <span style={{
            display: 'inline-block',
            height: 8,
            width: `${Math.round((micLevel || 0) * 100)}%`,
            background: micLevel > 0.02 ? '#5cb85c' : '#aaa',
            borderRadius: 4,
            transition: 'width 100ms linear'
          }} />
        </span>
        &nbsp;{Math.round((micLevel || 0) * 100)}%
      </div>

      {/* CONTROLES EN BAS */}
      <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #eee' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          <button onClick={toggleMute} disabled={!joined} style={{ background: '#6c43f3', color: 'white', border: 0, borderRadius: 6, padding: '10px 16px' }}>
            {muted ? 'Remettre le micro' : 'Couper le micro'}
          </button>
          <button onClick={toggleVideo} disabled={!joined} style={{ background: '#6c43f3', color: 'white', border: 0, borderRadius: 6, padding: '10px 16px' }}>
            {videoOn ? 'Couper la caméra' : 'Allumer la caméra'}
          </button>
          <button onClick={toggleShare} disabled={!joined} style={{ background: '#6c43f3', color: 'white', border: 0, borderRadius: 6, padding: '10px 16px' }}>
            {sharing ? 'Arrêter le partage' : 'Partager l’écran'}
          </button>

            {/* 🔴 Quitter */}
          <button
            onClick={handleLeave}
            disabled={!joined}
            style={{ background: '#a40000', color: '#fff', border: 0, borderRadius: 6, padding: '10px 16px' }}
          >
            Quitter
          </button>

          {/* Plus de démarrer/arrêter : auto */}
          {uiRole === 'professional' && (
            <button
              onClick={() => startSummaryPolling()}
              disabled={sumLoading}
              style={{ background: '#1976d2', color: '#fff', border: 0, borderRadius: 6, padding: '10px 16px' }}
            >
              {sumLoading ? 'Récupération…' : 'Rafraîchir le résumé'}
            </button>
          )}
        </div>

        <div style={{ marginTop: 8, color: '#666', fontSize: 13 }}>
          Participants présents (estimation) : {attendeeCount}
        </div>

        {pipelineId ? (
          <p style={{ marginTop: 8, color: '#666' }}>
            Pipeline : <code>{pipelineId}</code> — {pipelineStatus || '…'}
          </p>
        ) : null}
      </div>

      {/* Résumé (PRO uniquement) */}
      {proCanSeeSummary && (
        <div style={{ marginTop: 16, padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
          <h4 style={{ marginTop: 0 }}>Résumé automatique</h4>
          {sumError ? <div style={{ color: 'crimson' }}>{sumError}</div> : null}
          {!summary && !sumLoading && (
            <div style={{ color: '#666' }}>
              Aucun résumé encore disponible. La transcription s’arrête automatiquement à la fin de séance.
              Cliquez sur « Rafraîchir » après quelques secondes si besoin.
            </div>
          )}
          {sumLoading && <div>Chargement du résumé…</div>}

          {summary?.ready && (
            <>
              <div style={{ color: '#666', marginBottom: 8 }}>
                Mode : <strong>{summary?.transcription?.job_mode || '-'}</strong> — Langue :{' '}
                <strong>{summary?.transcription?.language_code || '-'}</strong>
              </div>

              <div style={{ whiteSpace: 'pre-line', background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                {humanNarrative.length ? (
                  humanNarrative.map((p, i) => <p key={i} style={{ marginBottom: 10 }}>{p}</p>)
                ) : (
                  <p style={{ color: '#666' }}>Analyse disponible, mais aucun élément saillant n’a été extrait.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Debug */}
      <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
        <div>Session : <code>{sessionId}</code></div>
        <div>MeetingId : <code>{
          meetingRef.current?.Meeting?.MeetingId ||
          meetingRef.current?.MeetingId || ''
        }</code></div>
        <div>AttendeeId : <code>{
          attendeeRef.current?.Attendee?.AttendeeId ||
          attendeeRef.current?.AttendeeId || ''
        }</code></div>
      </div>

      {/* Sortie audio */}
      <audio ref={audioRef} style={{ display: 'none' }} />

      <p style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
        Si la vidéo ne démarre pas, fermez les applications qui utilisent la caméra (Zoom, Meet…),
        puis rechargez la page. Vérifiez aussi Chrome → Paramètres → Confidentialité & sécurité →
        Paramètres des sites → Caméra / Micro (autoriser).
      </p>
    </div>
  );
}